import React from 'react';
import Icon from '../../../components/AppIcon';

const PersonalInsightsSection = () => {
  const insights = [
    {
      id: 1,
      title: "Problem-Solving Approach",
      icon: "Lightbulb",
      color: "primary",
      content: `My approach to problem-solving follows a structured methodology: First, I break down complex problems into smaller, manageable components. Then, I research existing solutions and best practices before designing a custom approach.\n\nI believe in the power of collaborative problem-solving. Some of my best solutions have emerged from brainstorming sessions with team members, where different perspectives illuminate aspects I might have missed. Documentation is crucial - I maintain detailed notes of problems encountered and solutions implemented for future reference.`,
      keyPoints: [
        "Break complex problems into smaller components",
        "Research existing solutions and best practices",
        "Collaborate with team members for diverse perspectives",
        "Document solutions for future reference"
      ]
    },
    {
      id: 2,
      title: "Continuous Learning Philosophy",
      icon: "BookOpen",
      color: "success",
      content: `Technology evolves rapidly, and staying current requires a systematic approach to learning. I dedicate at least 5 hours per week to learning new technologies, reading technical articles, and experimenting with emerging tools.\n\nI follow the 70-20-10 learning model: 70% learning through hands-on project work, 20% learning from others through mentorship and collaboration, and 10% formal education through courses and certifications. This balanced approach ensures practical application of theoretical knowledge.`,
      keyPoints: [
        "Dedicate 5+ hours weekly to learning",
        "Follow the 70-20-10 learning model",
        "Focus on practical application",
        "Stay updated with industry trends"
      ]
    },
    {
      id: 3,
      title: "Mentoring & Knowledge Sharing",
      icon: "Users",
      color: "brand-purple",
      content: `Mentoring junior developers has become one of my most rewarding professional activities. I've discovered that teaching others not only helps them grow but also deepens my own understanding of concepts.\n\nI organize monthly code review sessions where we discuss best practices, common pitfalls, and emerging patterns. My mentoring philosophy centers on empowering developers to find solutions independently while providing guidance and support when needed. I've seen mentees grow from junior developers to confident mid-level engineers.`,
      keyPoints: [
        "Mentor 5+ junior developers regularly",
        "Organize monthly code review sessions",
        "Focus on empowering independent problem-solving",
        "Track mentee progress and career growth"
      ]
    },
    {
      id: 4,
      title: "Code Quality & Best Practices",
      icon: "Code2",
      color: "accent",
      content: `Clean, maintainable code is not just a preference - it's a professional responsibility. I follow SOLID principles, implement comprehensive testing strategies, and maintain consistent coding standards across all projects.\n\nCode reviews are opportunities for learning and improvement, not criticism. I approach them with curiosity and openness, always looking for ways to enhance code quality, performance, and maintainability. Every line of code should tell a story and serve a clear purpose.`,
      keyPoints: [
        "Follow SOLID principles consistently",
        "Implement comprehensive testing strategies",
        "Maintain consistent coding standards",
        "Approach code reviews as learning opportunities"
      ]
    }
  ];

  const getColorClasses = (color) => {
    const colorMap = {
      primary: {
        bg: "bg-primary",
        text: "text-primary",
        light: "bg-primary/10",
        border: "border-primary/20"
      },
      success: {
        bg: "bg-success",
        text: "text-success",
        light: "bg-success/10",
        border: "border-success/20"
      },
      "brand-purple": {
        bg: "bg-brand-purple",
        text: "text-brand-purple",
        light: "bg-brand-purple/10",
        border: "border-brand-purple/20"
      },
      accent: {
        bg: "bg-accent",
        text: "text-accent",
        light: "bg-accent/10",
        border: "border-accent/20"
      }
    };
    return colorMap[color] || colorMap.primary;
  };

  return (
    <section className="py-20 bg-white">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Icon name="Heart" size={16} />
            <span>Personal Insights</span>
          </div>
          <h2 className="text-3xl lg:text-4xl font-bold text-text-primary mb-4">
            My Development Philosophy
          </h2>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto">
            The principles, approaches, and mindset that guide my work as a full-stack engineer 
            and shape my interactions with technology and teams.
          </p>
        </div>

        <div className="grid gap-8">
          {insights.map((insight, index) => {
            const colors = getColorClasses(insight.color);
            
            return (
              <div key={insight.id} className="bg-white rounded-2xl border border-border shadow-premium overflow-hidden">
                <div className="p-8">
                  <div className="flex items-start space-x-6">
                    <div className={`w-16 h-16 ${colors.bg} rounded-2xl flex items-center justify-center flex-shrink-0`}>
                      <Icon name={insight.icon} size={28} className="text-white" />
                    </div>
                    
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold text-text-primary mb-4">{insight.title}</h3>
                      
                      <div className="prose prose-lg max-w-none mb-6">
                        <p className="text-text-secondary leading-relaxed whitespace-pre-line">
                          {insight.content}
                        </p>
                      </div>

                      <div className={`${colors.light} rounded-xl p-6 border ${colors.border}`}>
                        <h4 className={`font-semibold ${colors.text} mb-4 flex items-center`}>
                          <Icon name="CheckCircle" size={18} className="mr-2" />
                          Key Principles
                        </h4>
                        <div className="grid md:grid-cols-2 gap-3">
                          {insight.keyPoints.map((point, pointIndex) => (
                            <div key={pointIndex} className="flex items-start space-x-2">
                              <Icon name="ArrowRight" size={16} className={`${colors.text} mt-0.5 flex-shrink-0`} />
                              <span className="text-text-secondary text-sm">{point}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Personal Mission Statement */}
        <div className="mt-16 bg-gradient-to-br from-primary to-brand-purple rounded-2xl p-8 text-white">
          <div className="text-center">
            <div className="w-20 h-20 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Icon name="Compass" size={36} className="text-white" />
            </div>
            <h3 className="text-2xl font-bold mb-4">Personal Mission Statement</h3>
            <p className="text-xl leading-relaxed max-w-4xl mx-auto opacity-90">
              "To create technology solutions that simplify complex problems, empower users to achieve their goals, 
              and contribute to a more connected and efficient world. I strive to write code that not only works 
              today but remains maintainable, scalable, and valuable for years to come."
            </p>
          </div>
        </div>

        {/* Values Grid */}
        <div className="mt-16 grid md:grid-cols-3 gap-8">
          <div className="text-center space-y-4">
            <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto">
              <Icon name="Shield" size={28} className="text-primary" />
            </div>
            <h4 className="text-xl font-bold text-text-primary">Integrity</h4>
            <p className="text-text-secondary">
              Honest communication, ethical coding practices, and transparent project management 
              build trust with clients and team members.
            </p>
          </div>

          <div className="text-center space-y-4">
            <div className="w-16 h-16 bg-success/10 rounded-2xl flex items-center justify-center mx-auto">
              <Icon name="Zap" size={28} className="text-success" />
            </div>
            <h4 className="text-xl font-bold text-text-primary">Innovation</h4>
            <p className="text-text-secondary">
              Embracing new technologies and methodologies while maintaining focus on practical, 
              user-centered solutions that deliver real value.
            </p>
          </div>

          <div className="text-center space-y-4">
            <div className="w-16 h-16 bg-brand-purple/10 rounded-2xl flex items-center justify-center mx-auto">
              <Icon name="Heart" size={28} className="text-brand-purple" />
            </div>
            <h4 className="text-xl font-bold text-text-primary">Empathy</h4>
            <p className="text-text-secondary">
              Understanding user needs, supporting team members, and creating inclusive 
              technology that serves diverse communities and use cases.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PersonalInsightsSection;